// src/hooks/useDimensions.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createDimension,
  deleteDimension,
  getDimension,
  listDimensions,
  setDimensionStatus,
  updateDimension,
} from "@/services/dimensions";
import type { DimensionUpsertIn } from "@/types/dimensions";

export function useDimensions(params?: {
  q?: string;
  risk_theme?: string;
  is_active?: boolean;
}) {
  return useQuery({
    queryKey: ["dimensions", params?.q ?? "", params?.risk_theme ?? "", params?.is_active],
    queryFn: () => listDimensions(params),
  });
}

export function useDimensionDetail(id?: string) {
  return useQuery({
    queryKey: ["dimension_detail", id],
    queryFn: () => getDimension(id as string),
    enabled: !!id,
  });
}

export function useCreateDimension() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DimensionUpsertIn) => createDimension(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dimensions"] });
    },
  });
}

export function useUpdateDimension() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DimensionUpsertIn }) => updateDimension(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["dimensions"] });
      qc.invalidateQueries({ queryKey: ["dimension_detail", variables.id] });
    },
  });
}

export function useSetDimensionStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) => setDimensionStatus(id, is_active),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["dimensions"] });
      qc.invalidateQueries({ queryKey: ["dimension_detail", variables.id] });
    },
  });
}

export function useDeleteDimension() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDimension(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dimensions"] });
    },
  });
}
